import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Text, VStack } from '@adc/parallax-component-library';
import i18n from '../../utilities/i18n';
import * as log from '../../utilities/log';
import { urls } from '../../utilities/dataHelpers';

export type QrCodeProps = {
  organizationIdentifier: string;
  studyIdentifier: string;
  siteIdentifier?: string;
  subjectIdentifier?: string;
  authorizationCode?: string;
  organizationKey: string;
  studyKey: string;
  siteKey?: string;
  subjectKey?: string;
  uploadFrequency?: number;
};

type QrCodeValue = {
  organizationId: string;
  studyId: string;
  siteId?: string;
  subjectId?: string;
  authorizationCode?: string;
  protected: {
    organizationKey: string;
    studyKey: string;
    siteKey: string;
    subjectKey: string;
    uploadFrequency: number;
    config: string;
    baseUrl: string;
  };
};

/**
 * Generates a QR code containing the study information and any additional optional details.
 * It displays the QR code along with labels for any of these provided values.
 * The protected section is encrypted with a private encryption key for which the mobile app has the public key.
 * This QR code can be scanned in the mobile app to automatically populate the fields from the non-protected section.
 * @param organizationIdentifier - The identifier for the organization, displayed to the user, used for mobile app field population.
 * @param studyIdentifier - The identifier for the study, displayed to the user, used for mobile app field population.
 * @param siteIdentifier - The identifier for the site (optional), displayed to the user, used for mobile app field population.
 * @param subjectIdentifier - The identifier for the subject (optional), displayed to the user, used for mobile app field population.
 * @param authorizationCode - The authorization code (optional), not displayed, used for for mobile app field population.
 * @param uploadFrequency - The custom upload frequency (optional), not displayed, used for overriding the core module upload frequency.
 * @param organizationKey - The unique key of the organization, not displayed, used for device registration and validation.
 * @param studyKey - The unique key of the study, not displayed, used for core module and API authentication.
 * @param siteKey - The unique key of the site (optional), not displayed, used for core module and API authentication.
 * @param subjectKey - The unique key of the subject (optional), not displayed, used for core module and API authentication.
 * @returns {React.JSX.Element}
 */
export default function QrCode({
  organizationIdentifier,
  studyIdentifier,
  siteIdentifier,
  subjectIdentifier,
  authorizationCode,
  organizationKey,
  studyKey,
  siteKey,
  subjectKey,
  uploadFrequency,
}: QrCodeProps) {
  const qrCodeSvgValue: QrCodeValue = {
    organizationId: organizationIdentifier,
    studyId: studyIdentifier,
    siteId: siteIdentifier,
    subjectId: subjectIdentifier,
    authorizationCode: authorizationCode,
    protected: {
      organizationKey: organizationKey,
      studyKey: studyKey,
      siteKey: siteKey ?? '',
      subjectKey: subjectKey ?? '',
      uploadFrequency: uploadFrequency ?? 0, // 0 uses default frequency in core module
      config: '',
      baseUrl: urls.api,
    },
  };

  log.debug('QR Code generated with value:', qrCodeSvgValue);

  return (
    <VStack gap="$4" testID="qr-code">
      <VStack justifyContent="center" alignItems="center">
        <QRCodeSVG
          value={JSON.stringify(qrCodeSvgValue)}
          height="100%"
          width="100%"
        />
      </VStack>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          textAlign: 'left',
          flex: 1,
          height: '100%',
          rowGap: 'var(--size-xs)',
          columnGap: 'var(--size-xs)',
        }}
      >
        <Text type="$bodySmall.strong">
          {i18n.t('QrCode.organizationIdentifier')}
        </Text>
        <Text type="$bodySmall.default" wordWrap="break-word" overflow="hidden">
          {organizationIdentifier}
        </Text>
        <Text type="$bodySmall.strong">{i18n.t('QrCode.studyIdentifier')}</Text>
        <Text type="$bodySmall.default" wordWrap="break-word" overflow="hidden">
          {studyIdentifier}
        </Text>
        {siteIdentifier ? (
          <>
            <Text type="$bodySmall.strong">
              {i18n.t('QrCode.siteIdentifier')}
            </Text>
            <Text
              type="$bodySmall.default"
              wordWrap="break-word"
              overflow="hidden"
            >
              {siteIdentifier}
            </Text>
          </>
        ) : null}
        {subjectIdentifier ? (
          <>
            <Text type="$bodySmall.strong">
              {i18n.t('QrCode.subjectIdentifier')}
            </Text>
            <Text
              type="$bodySmall.default"
              wordWrap="break-word"
              overflow="hidden"
            >
              {subjectIdentifier}
            </Text>
          </>
        ) : null}
      </div>
    </VStack>
  );
}
